import { useCallback, useEffect, useRef, useState } from "react";
import {
  approvalRequests,
  type BulkApprovalResponse,
  type BulkDecisionOptions,
} from "../../../../api/approvalRequests.service.js";
import {
  APPROVAL_STATUS_EXPECTED,
  APPROVAL_STATUS_MAX_IDS_PER_CALL,
  EMPTY_TOTALS,
  mergeTotals,
  planApprovalStatusChunks,
  totalsOf,
  withUnknownChunk,
  type ApprovalRequestStatusRow,
  type ApprovalStatusChunk,
  type ApprovalStatusRunTotals,
  type ApprovalStatusTarget,
} from "./approvalRequestStatus.js";
import { preventUnload } from "./batchRunHelpers.js";
import { describeApiError } from "./describeApiError.js";

export type ApprovalRequestStatusBatchProgress = {
  chunk: number;
  totalChunks: number;
  processedRows: number;
  unknownRows: number;
  totalRows: number;
  status: ApprovalStatusTarget | null;
};

const EMPTY_PROGRESS: ApprovalRequestStatusBatchProgress = {
  chunk: 0,
  totalChunks: 0,
  processedRows: 0,
  unknownRows: 0,
  totalRows: 0,
  status: null,
};

const GUARD_OPTIONS: BulkDecisionOptions = { expectedStatus: APPROVAL_STATUS_EXPECTED };

export const GUARD_UNSUPPORTED_MESSAGE =
  "approval-api does not support the NEW-only guard (its response has no statusMismatches field), nothing was sent. Deploy the approval-api change first.";

type Decide = (ids: string[], options: BulkDecisionOptions) => Promise<BulkApprovalResponse>;

const DECIDE: Record<ApprovalStatusTarget, Decide> = {
  APPROVED: (ids, options) => approvalRequests.bulkApprove(ids, options),
  REJECTED: (ids, options) => approvalRequests.bulkReject(ids, options),
  SUSPENDED: (ids, options) => approvalRequests.bulkSuspend(ids, options),
};

function chunkLabel(chunk: number, totalChunks: number, current: ApprovalStatusChunk): string {
  return `Chunk ${chunk} / ${totalChunks} (${current.ids.length} ${current.status} ids)`;
}

function chunkFailureMessage(
  err: unknown,
  chunk: number,
  totalChunks: number,
  current: ApprovalStatusChunk,
): string {
  const detail = describeApiError(err, "Bulk decision failed");
  return `${chunkLabel(chunk, totalChunks, current)} failed: ${detail}. This chunk's state is unknown and its ids are listed as unknown; earlier chunks are written and later ones were not sent. Re-running the same file is safe: ids already decided come back as skipped (not NEW).`;
}

function guardLostMessage(chunk: number, totalChunks: number, current: ApprovalStatusChunk) {
  return `${chunkLabel(chunk, totalChunks, current)} came back without statusMismatches: approval-api did not enforce the NEW-only guard on it, so requests in other decidable statuses may have been decided. The run stopped; later chunks were not sent. Check this chunk's decided ids.`;
}

async function guardSupported(status: ApprovalStatusTarget): Promise<boolean> {
  const probe = await DECIDE[status]([crypto.randomUUID()], GUARD_OPTIONS);
  return probe.statusMismatches !== undefined;
}

export function useApprovalRequestStatusBatch() {
  const [running, setRunning] = useState(false);
  const [stopped, setStopped] = useState(false);
  const [progress, setProgress] = useState<ApprovalRequestStatusBatchProgress>(EMPTY_PROGRESS);
  const [totals, setTotals] = useState<ApprovalStatusRunTotals>(EMPTY_TOTALS);
  const [error, setError] = useState<string | null>(null);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!running) return;
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [running]);

  useEffect(() => {
    return () => {
      stopRef.current = true;
    };
  }, []);

  const stop = useCallback(() => {
    stopRef.current = true;
  }, []);

  const reset = useCallback(() => {
    setStopped(false);
    setProgress(EMPTY_PROGRESS);
    setTotals(EMPTY_TOTALS);
    setError(null);
  }, []);

  const start = useCallback(async (rows: ApprovalRequestStatusRow[]) => {
    const chunks = planApprovalStatusChunks(rows, APPROVAL_STATUS_MAX_IDS_PER_CALL);
    if (chunks.length === 0) return;
    const totalChunks = chunks.length;
    const totalRows = rows.length;

    stopRef.current = false;
    setRunning(true);
    setStopped(false);
    setError(null);
    setTotals(EMPTY_TOTALS);
    setProgress(EMPTY_PROGRESS);

    let accumulated = EMPTY_TOTALS;
    let processedRows = 0;
    let unknownRows = 0;

    try {
      try {
        if (!(await guardSupported(chunks[0].status))) {
          setError(GUARD_UNSUPPORTED_MESSAGE);
          return;
        }
      } catch (err) {
        setError(
          `Guard probe failed: ${describeApiError(err, "Bulk decision failed")}. Nothing was sent.`,
        );
        return;
      }
      setProgress({ ...EMPTY_PROGRESS, totalChunks, totalRows });

      for (const [index, current] of chunks.entries()) {
        if (stopRef.current) {
          setStopped(true);
          break;
        }
        const chunk = index + 1;
        const at = { chunk, totalChunks, totalRows, status: current.status };
        setProgress({ ...at, processedRows, unknownRows });

        try {
          const result = await DECIDE[current.status](current.ids, GUARD_OPTIONS);
          const chunkTotals = totalsOf(current.status, result);
          accumulated = mergeTotals(accumulated, chunkTotals);
          setTotals(accumulated);
          processedRows += current.ids.length;
          setProgress({ ...at, processedRows, unknownRows });
          if (chunkTotals.guardUnconfirmed) {
            setError(guardLostMessage(chunk, totalChunks, current));
            break;
          }
        } catch (err) {
          accumulated = withUnknownChunk(accumulated, current);
          setTotals(accumulated);
          unknownRows += current.ids.length;
          setProgress({ ...at, processedRows, unknownRows });
          setError(chunkFailureMessage(err, chunk, totalChunks, current));
          break;
        }
      }
    } finally {
      setRunning(false);
    }
  }, []);

  return { running, stopped, progress, totals, error, start, stop, reset };
}
